import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/arcondes-logo-white.png";
import { openConsentPreferences } from "@/lib/consent";

const serviceKeys = [
  { key: "facadeInspections", path: "/services/facade-inspection" },
  { key: "parapetInspections", path: "/services/parapet-inspection" },
  { key: "leakInvestigations", path: "/services/leak-investigation" },
  { key: "structuralRepairs", path: "/services/structural-repairs" },
  { key: "engineeringReports", path: "/services/engineering-reports" },
  { key: "dobViolations", path: "/services/dob-violations" },
  { key: "constructionContracting", path: "/services/construction-consulting" },
];

const quickLinkKeys = [
  { key: "home", path: "/" },
  { key: "localLaw11", path: "/local-law-11" },
  { key: "aboutUs", path: "/about" },
  { key: "services", path: "/services" },
  { key: "projects", path: "/projects" },
  { key: "blog", path: "/blog" },
  { key: "resources", path: "/resources" },
  { key: "areasWeServe", path: "/areas-we-serve" },
  { key: "contact", path: "/contact" },
];

const fispDeadlines = [
  { sub: "10A", deadline: "Feb 21, 2027", statusKey: "active" },
  { sub: "10B", deadline: "Feb 21, 2028", statusKey: "starting2026" },
  { sub: "10C", deadline: "Feb 21, 2029", statusKey: "upcoming" },
];

const Footer = () => {
  const { t, i18n } = useTranslation("common");
  const prefix = i18n.language === "es" ? "/es" : "";

  const localizedPath = (path: string) => `${prefix}${path}`;

  return (
    <footer className="bg-foreground text-primary-foreground pb-24 sm:pb-16">
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={logo} alt="ARCONDES" width={666} height={375} className="h-16 w-auto mb-4" />
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-4">
              {t("footer.brand")}
            </p>
            <div className="space-y-2">
              <a href="tel:+13476131717" className="flex items-center gap-2 text-primary-foreground/60 text-sm hover:text-brand-yellow transition-colors">
                <Phone className="h-3.5 w-3.5" />347-613-1717
              </a>
              <a href="mailto:info@arcondes.com" className="flex items-center gap-2 text-primary-foreground/60 text-sm hover:text-brand-yellow transition-colors">
                <Mail className="h-3.5 w-3.5" />info@arcondes.com
              </a>
              <a href="https://www.instagram.com/arcondes_nyc" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary-foreground/60 text-sm hover:text-brand-yellow transition-colors">
                <Instagram className="h-3.5 w-3.5" />@arcondes_nyc
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.servicesTitle")}</h4>
            <ul className="space-y-2">
              {serviceKeys.map((service) => (
                <li key={service.key}>
                  <Link to={localizedPath(service.path)} className="text-primary-foreground/60 text-sm hover:text-brand-yellow transition-colors">
                    {t(`footer.services.${service.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.quickLinksTitle")}</h4>
            <ul className="space-y-2">
              {quickLinkKeys.map((link) => (
                <li key={link.key}>
                  <Link to={localizedPath(link.path)} className="text-primary-foreground/60 text-sm hover:text-brand-yellow transition-colors">
                    {t(`footer.quickLinks.${link.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* FISP Deadlines */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.fispTitle")}</h4>
            <div className="space-y-3 mb-6">
              {fispDeadlines.map((d) => (
                <div key={d.sub} className="flex justify-between items-center text-sm">
                  <span className="text-primary-foreground/80 font-medium">{t("footer.subCycle", { sub: d.sub })}</span>
                  <span className="text-brand-yellow font-semibold">{d.deadline}</span>
                </div>
              ))}
            </div>
            <Link to={localizedPath("/local-law-11")} className="text-brand-yellow text-sm font-semibold hover:text-brand-yellow/80 transition-colors">
              {t("footer.checkDeadline")}
            </Link>

            <div className="mt-6 pt-6 border-t border-primary-foreground/10">
              <div className="flex items-start gap-3 text-primary-foreground/60 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {t("footer.serviceAreas")}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-primary-foreground/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-primary-foreground/60 text-xs">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
          <div className="flex flex-wrap gap-6">
            <Link to={localizedPath("/privacy")} className="text-primary-foreground/60 text-xs hover:text-brand-yellow transition-colors">{t("footer.privacyPolicy")}</Link>
            <Link to={localizedPath("/terms")} className="text-primary-foreground/60 text-xs hover:text-brand-yellow transition-colors">{t("footer.termsOfService")}</Link>
            <button onClick={openConsentPreferences} className="text-primary-foreground/60 text-xs hover:text-brand-yellow transition-colors">Cookie Preferences</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
